import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import apiService from '../../services/api';

const Title = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
    {' '}
    {children}
  </Typography>
);

const ExhibitorDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exhibitor, setExhibitor] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.getExhibitorById(Number(id));
      setExhibitor(response);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const maskId = (value?: string) => {
    if (!value || value.length <= 4) return value || 'N/A';
    const last4 = value.slice(-4);
    const masked = '*'.repeat(value.length - 4);
    return masked + last4;
  };

  if (loading || !exhibitor)
    return (
      <Typography sx={{ p: 3 }}>
        <CircularProgress />{' '}
      </Typography>
    );

  return (
    <Box sx={{ p: 4 }}>
      <Button variant="outlined" onClick={() => navigate('/exhibitors')}>
        Back to List
      </Button>

      {/* PAGE HEADER WITH IMAGE */}
      <Grid container alignItems="center" sx={{ mt: 3, mb: 4 }}>
        <Grid item xs={12} sm={8}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Exhibitor Profile
          </Typography>
        </Grid>
      </Grid>

      {/* Company Details */}
      <Title>Company Details</Title>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Company / Fascia Name:</strong> {exhibitor.fasciaName}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Address:</strong> {exhibitor.companyAddress ?? 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>GST Number:</strong>{' '}
            {exhibitor.gstin ? maskId(exhibitor.gstin) : 'N/A'}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>PAN Number:</strong>{' '}
            {exhibitor.companyPan ? maskId(exhibitor.companyPan) : 'N/A'}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Office Number:</strong> {exhibitor.officeNumber ?? 'N/A'}
          </Typography>
        </Grid>
      </Grid>

      <Title>Booth Details</Title>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Booth Number:</strong> {exhibitor.boothNumber ?? 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Booth Type:</strong> {exhibitor.boothType ?? 'N/A'}
          </Typography>
        </Grid>
      </Grid>

      {/* Type of Organization */}
      <Title>Type of Organization</Title>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip label={exhibitor.isStartupCompany ? 'Startup' : 'Corporate'} />
      </Box>

      {/* Categories */}
      <Title>Business Categories</Title>
      {exhibitor.categories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {exhibitor.categories?.map((cat: any) => (
            <Chip
              key={cat.category.id}
              label={cat.category.name}
              sx={{ background: cat.category.categoryColor, color: '#050404' }}
            />
          ))}
        </Box>
      )}

      {/* Major Export Country */}
      <Title>Major Export Country</Title>
      <Typography>{exhibitor.majorExportMarkets ?? 'N/A'}</Typography>

      {/* Product Range */}
      <Title>Product Range & Area of Specialisation</Title>
      <Typography>{exhibitor.productRangeSpecialisation ?? 'N/A'}</Typography>

      {/* International Clients */}
      <Title>Major International Clients / Projects</Title>
      <Typography>{exhibitor.majorInternationalClients ?? 'N/A'}</Typography>

      {/* New Products */}
      <Title>New Product Launch</Title>
      <Typography>{exhibitor.newProductsLaunch ?? 'N/A'}</Typography>

      {/* Company Profile */}
      <Title>Company Profile</Title>
      <Typography sx={{ whiteSpace: 'pre-line' }}>
        {exhibitor.companyProfile ?? 'N/A'}
      </Typography>

      {/* Contact Details */}
      <Title>Contact Details</Title>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Representative:</strong> {exhibitor.salutation}{' '}
            {exhibitor.representativeName}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Designation:</strong>{' '}
            {exhibitor.representativeDesignation || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Email:</strong> {exhibitor.user?.email}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>
            <strong>Phone:</strong> {exhibitor.user?.phone ?? 'N/A'}
          </Typography>
        </Grid>
      </Grid>

      {/* Status */}
      <Title>Status</Title>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={exhibitor.user?.isActive ? 'Active' : 'Inactive'}
          color={exhibitor.user?.isActive ? 'success' : 'error'}
        />
        <Chip
          label={exhibitor.isVerified ? 'Verified' : 'Not Verified'}
          color={exhibitor.isVerified ? 'success' : 'warning'}
        />
        <Chip
          label={exhibitor.isFeatured ? 'Featured Exhibitor' : 'Standard'}
          color={exhibitor.isFeatured ? 'primary' : 'default'}
        />
      </Box>
    </Box>
  );
};

export default ExhibitorDetailsPage;
